import {
  DEFAULT_FUNDING_PCT,
  DEFAULT_REV_SHARE_PCT,
} from '../constants';
import { isPlanMonthOutsideCommitment } from './resolveFundingCommitment';
import {
  isRevSharePctConfigured,
  resolveActualFundingPct,
  resolveEffectiveRevSharePctFromParts,
  resolveProRataRevSharePct,
} from './resolveProRataRevShare';

export {
  isRevSharePctConfigured,
  resolveProRataRevSharePct,
  PRO_RATA_REV_SHARE_TOOLTIP,
} from './resolveProRataRevShare';

export function isMarketingPlanManualMode(state) {
  return state?.marketingPlanManualMode === true;
}

export function shouldApplyDrawLimits(state) {
  return !isMarketingPlanManualMode(state);
}

export function resolveDefaultFundingPct(state) {
  return Number(state?.defaultFundingPct ?? DEFAULT_FUNDING_PCT) || 0;
}

export function resolveDefaultRevSharePct(state) {
  if (!isRevSharePctConfigured(state?.defaultRevSharePct)) {
    return null;
  }
  return Number(state.defaultRevSharePct);
}

export function resolveConfiguredRevSharePct(state, planIndex) {
  if (isPlanMonthOutsideCommitment(state, planIndex)) {
    return null;
  }

  if (isMarketingPlanManualMode(state)) {
    const stored = state?.revSharePct?.[planIndex];
    return isRevSharePctConfigured(stored) ? Number(stored) : null;
  }

  if (isRevSharePctConfigured(state?.defaultRevSharePct)) {
    return Number(state.defaultRevSharePct);
  }

  const stored = state?.revSharePct?.[planIndex];
  return isRevSharePctConfigured(stored) ? Number(stored) : null;
}

export function resolveCohortDraw(state, planIndex) {
  if (isPlanMonthOutsideCommitment(state, planIndex)) {
    return 0;
  }

  const marketing = Number(state?.marketing?.[planIndex]) || 0;
  if (marketing <= 0) {
    return 0;
  }

  if (isMarketingPlanManualMode(state)) {
    const drawAmount = Number(state?.planDrawAmount?.[planIndex]);
    if (!Number.isFinite(drawAmount) || drawAmount <= 0) {
      return 0;
    }
    return Math.min(Math.round(drawAmount), marketing);
  }

  const fundingPct = Number(state?.fundingPct?.[planIndex]) || 0;
  return Math.min(Math.round((marketing * fundingPct) / 100), marketing);
}

export function resolveEffectiveRevSharePct(state, planIndex, actualFundingPct = null) {
  if (isPlanMonthOutsideCommitment(state, planIndex)) {
    return 0;
  }

  const configured = resolveConfiguredRevSharePct(state, planIndex);
  const fallbackFundingPct =
    Number(state?.fundingPct?.[planIndex]) || resolveDefaultFundingPct(state);

  return resolveEffectiveRevSharePctFromParts({
    configuredRevSharePct: configured,
    actualFundingPct,
    fallbackFundingPct,
  });
}

export function resolveEffectiveFundingPct(state, planIndex) {
  const marketing = Number(state?.marketing?.[planIndex]) || 0;
  if (marketing <= 0) {
    return 0;
  }

  const draw = resolveCohortDraw(state, planIndex);
  if (draw <= 0) {
    return 0;
  }

  return Math.min(100, Math.round((draw * 100) / marketing));
}

export function stateHasResolvedDraws(state) {
  return (state?.marketing ?? []).some(
    (_, planIndex) => resolveCohortDraw(state, planIndex) > 0
  );
}

export function seedPlanDrawAmountFromFundingPct(state) {
  const marketing = state?.marketing ?? [];
  return marketing.map((spend, planIndex) => {
    if (isPlanMonthOutsideCommitment(state, planIndex)) {
      return null;
    }
    const drawAmount = resolveCohortDraw(
      { ...state, marketingPlanManualMode: false },
      planIndex
    );
    return drawAmount > 0 ? drawAmount : null;
  });
}
